/**
 * WorkflowSettingsSection - Configure production workflow behavior
 *
 * Allows configuring:
 * - OOS Handling Mode: How to handle out-of-stock garments during catching
 * - Bin Confirmation Mode: Whether catcher must re-scan bin after placing item
 * - Max Batch Size: Maximum orders allowed per batch
 *
 * Design considerations for production floor use:
 * - Large touch targets (44px minimum for gloved hands)
 * - High contrast text for warehouse lighting
 * - Clear radio button labels with descriptions
 * - Accessible with proper ARIA attributes
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Settings2,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Save,
  Package,
  ScanLine,
  Layers,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

// Match database enum values
type OOSHandlingMode = 'HOLD_IN_BIN' | 'MOVE_TO_OOS_BIN';
type BinConfirmationMode = 'trust' | 'confirm';

interface WorkflowSettingsSectionProps {
  /** When true, renders without Card wrapper for embedding in accordions */
  embedded?: boolean;
}

export function WorkflowSettingsSection({ embedded = false }: WorkflowSettingsSectionProps) {
  const { t } = useTranslation('settings');
  const [oosMode, setOosMode] = useState<OOSHandlingMode>('HOLD_IN_BIN');
  const [binConfirmMode, setBinConfirmMode] = useState<BinConfirmationMode>('confirm');
  const [maxBatchSize, setMaxBatchSize] = useState(50);
  const [hasChanges, setHasChanges] = useState(false);

  // Query
  const settingsQuery = trpc.productionSettings.getWorkflowSettings.useQuery();

  // Mutation
  const updateMutation = trpc.productionSettings.updateWorkflowSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.workflow.saved'));
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Sync local state with server data
  useEffect(() => {
    if (settingsQuery.data) {
      setOosMode(settingsQuery.data.oosHandlingMode);
      setBinConfirmMode(settingsQuery.data.binConfirmationMode);
      setMaxBatchSize(settingsQuery.data.maxBatchSize);
      setHasChanges(false);
    }
  }, [settingsQuery.data]);

  const handleOosModeChange = (value: OOSHandlingMode) => {
    setOosMode(value);
    setHasChanges(true);
  };

  const handleBinConfirmModeChange = (value: BinConfirmationMode) => {
    setBinConfirmMode(value);
    setHasChanges(true);
  };

  const handleMaxBatchSizeChange = (value: number) => {
    const clamped = Math.min(200, Math.max(10, value));
    setMaxBatchSize(clamped);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      oosHandlingMode: oosMode,
      binConfirmationMode: binConfirmMode,
      maxBatchSize,
    });
  };

  const isLoading = settingsQuery.isLoading;
  const hasError = settingsQuery.isError;

  // Save button (used in both modes)
  const saveButton = hasChanges ? (
    <Button
      onClick={handleSave}
      disabled={updateMutation.isPending}
      className="h-12 gap-2"
    >
      {updateMutation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      {t('production.common.saveChanges')}
    </Button>
  ) : null;

  // Content to render (shared between embedded and standalone modes)
  const content = (
    <div className="space-y-6">
        {/* Error State */}
        {hasError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Failed to load workflow settings</AlertTitle>
            <AlertDescription className="flex items-center gap-2">
              {settingsQuery.error?.message || 'Unknown error'}
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() => settingsQuery.refetch()}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading workflow settings...</span>
          </div>
        )}

        {!isLoading && !hasError && (
          <>
            {/* OOS Handling Mode */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Label className="text-base font-medium">Out-of-Stock Handling</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose how to handle orders when a garment is out of stock during catching
              </p>
              <RadioGroup
                value={oosMode}
                onValueChange={(v) => handleOosModeChange(v as OOSHandlingMode)}
                className="space-y-3"
              >
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="HOLD_IN_BIN" id="oos-hold" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="oos-hold" className="font-medium cursor-pointer">
                      Option A: Hold in Regular Bin
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Keep the garment in its assigned bin with HELD status.
                      The bin is blocked until the OOS item is restocked and the order can complete.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="MOVE_TO_OOS_BIN" id="oos-move" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="oos-move" className="font-medium cursor-pointer">
                      Option B: Move to OOS Bin
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Move the garment to a dedicated OOS bin, freeing up the regular bin for other orders.
                      Requires OOS bins to be configured.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <Separator />

            {/* Bin Confirmation Mode */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <ScanLine className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Label className="text-base font-medium">Bin Confirmation Mode</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Whether the catcher must re-scan the bin after placing an item
              </p>
              <RadioGroup
                value={binConfirmMode}
                onValueChange={(v) => handleBinConfirmModeChange(v as BinConfirmationMode)}
                className="space-y-3"
              >
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="trust" id="bin-trust" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="bin-trust" className="font-medium cursor-pointer">
                      Trust Mode (Faster)
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      System trusts that the catcher placed the item in the displayed bin.
                      Faster workflow, but relies on catcher accuracy.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="confirm" id="bin-confirm" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="bin-confirm" className="font-medium cursor-pointer">
                      Confirm Mode (More Accurate)
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Catcher must re-scan the bin QR code after placing the item.
                      Adds an extra step but ensures correct placement.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <Separator />

            {/* Max Batch Size */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Label className="text-base font-medium">Maximum Batch Size</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Maximum number of orders that can be added to a single batch (10-200)
              </p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={10}
                  max={200}
                  value={maxBatchSize}
                  onChange={(e) => handleMaxBatchSizeChange(parseInt(e.target.value) || 50)}
                  className="w-24 h-12 text-lg"
                  aria-describedby="max-batch-help"
                />
                <span className="text-sm text-muted-foreground">orders per batch</span>
              </div>
              <p id="max-batch-help" className="text-xs text-muted-foreground">
                Note: This is the hard limit for manual batch creation.
                The &apos;Auto-batch default size&apos; in Picking (saved per browser) is used for auto-batching; this org-wide cap always wins.
              </p>
            </div>
          </>
        )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {updateMutation.isSuccess && 'Workflow settings saved successfully'}
        {hasChanges && 'You have unsaved changes'}
      </div>
    </div>
  );

  // If embedded, just return the content with save button at top
  if (embedded) {
    return (
      <div className="space-y-4">
        {saveButton && (
          <div className="flex justify-end">
            {saveButton}
          </div>
        )}
        {content}
      </div>
    );
  }

  // Standalone mode with Card wrapper
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-lg">Workflow Settings</CardTitle>
          </div>
          {saveButton}
        </div>
        <CardDescription>
          Configure how the production workflow handles special cases like out-of-stock items and bin verification
        </CardDescription>
      </CardHeader>

      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
